import { useEffect, useRef } from 'react';

import { ESCAPE, TAB } from '../utils/keyNames';
import { MOUSEDOWN, KEYDOWN, } from '../utils/eventNames'

// this hook traps keyboard focus inside of the modal, preventing keyboard users from inadvertently leaving the modal when they are trying to navigate it, which can be confusing.
// it also ensures that pressing ESCAPE or clicking outside of the modal will close it, and returns focus to the triggering element when closed

// it should be used inside of a modal like so:
// const { modalRef } = useModalAccessibility(handleCloseModal);
// at the top of the component definition, before useState, or any other hooks,
// where handleCloseModal is defined as a prop or inside the modal component,
// and then the modal element should be passed the following props:
// aria-labelledby={id of element with modal title}
// aria-modal="true"
// ref={modalRef}
// role="dialog"
// tabIndex={-1}
//
// If the modal is mounted unconditionally and only renders null when closed
// (a common pattern for `isOpen` props), pass `isOpen` (or any boolean that's
// only true while the modal is rendered) as the second argument. Otherwise
// the document listeners and focus-restoration run while the modal is
// closed, which can steal focus on every parent re-render.

// Overlays portaled to <body> on top of a modal live outside the modal DOM, so interacting
// with them must not count as leaving the modal. An overlay must be listed here to be exempt.
const PORTALED_OVERLAY_SELECTOR = '.MuiPickersPopper-root, .MuiDialog-root, .demo-blocked-modal-container, .sortable-drag-overlay';

const useModalAccessibility = (handleClose, enabled = true) => {
  const modalRef = useRef(null);
  const triggerElementRef = useRef<Element | null>(null);
  const handleCloseRef = useRef(handleClose);

  // Keep the ref pointed at the latest handleClose so the effect doesn't have
  // to depend on its identity (callers don't need to memoize).
  handleCloseRef.current = handleClose;

  const trapFocus = (event) => {
    const focusableModalElements = modalRef.current?.querySelectorAll('input, button:not(:disabled), select, textarea, a[href]');
    if (!focusableModalElements?.length) { return; }

    const firstElement = focusableModalElements[0];
    const lastElement = focusableModalElements[focusableModalElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      lastElement.focus();
      event.preventDefault();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      firstElement.focus();
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (!enabled) { return; }

    // Capture the element that triggered the modal before focusing the modal
    triggerElementRef.current = document.activeElement;

    if (modalRef.current) {
      modalRef.current.focus();
    }

    const handleDocumentClick = (event) => {
      if (event.target instanceof Element) {
        const overlay = event.target.closest(PORTALED_OVERLAY_SELECTOR);
        // an overlay that wraps this modal is its own chrome, not something stacked above it
        if (overlay && !overlay.contains(modalRef.current)) { return; }
      }

      if (modalRef.current && !modalRef.current.contains(event.target)) {
        handleCloseRef.current();
      }
    };

    const handleDocumentKeyDown = (event) => {
      if (event.key === ESCAPE) {
        // not gating on event.defaultPrevented — react-select preventDefaults Escape even
        // when its menu is closed, which would permanently disable Escape-to-close
        const stackedAbove = Array.from(document.querySelectorAll(PORTALED_OVERLAY_SELECTOR)).some(overlay =>
          !overlay.contains(modalRef.current) && !(modalRef.current && modalRef.current.contains(overlay)));
        if (stackedAbove) { return; }
        handleCloseRef.current();
      } else if (event.key === TAB) {
        trapFocus(event);
      }
    };

    document.addEventListener(MOUSEDOWN, handleDocumentClick);
    document.addEventListener(KEYDOWN, handleDocumentKeyDown);

    return () => {
      document.removeEventListener(MOUSEDOWN, handleDocumentClick);
      document.removeEventListener(KEYDOWN, handleDocumentKeyDown);

      // Restore focus to the element that triggered the modal
      if (triggerElementRef.current instanceof HTMLElement) {
        triggerElementRef.current.focus();
      }
    };
  }, [enabled]);

  return { modalRef };
};

export default useModalAccessibility;
